import React, { useState, useEffect, useRef } from 'react';
import VariablesSection from './VariablesSection';
import EmojiPickerButton from './EmojiPickerButton';
import { preferencesApi } from '../../services/emailApi';
import '../TemplateManagement.css';

/**
 * BasicInfoSection — Simplified template details
 * - Template Name (required)
 * - Template Code (auto-generated, editable)
 * - Subject Line (required, supports {{variables}})
 * - Variable Type selector (name / number) — WhatsApp pattern
 * - isActive toggle
 * - Embedded VariablesSection
 */
const BasicInfoSection = ({ formData, errors, onChange }) => {

  const subjectInputRef = useRef(null);

  const handleSubjectEmoji = (emoji) => {
    const input = subjectInputRef.current;
    const currentVal = formData.subject || '';
    if (input) {
      const cursorPos = input.selectionStart ?? currentVal.length;
      const before = currentVal.substring(0, cursorPos);
      const after = currentVal.substring(cursorPos);
      onChange({ subject: before + emoji + after });
      // Restore focus and cursor position after emoji insert
      requestAnimationFrame(() => {
        input.focus();
        const newPos = cursorPos + emoji.length;
        input.setSelectionRange(newPos, newPos);
      });
    } else {
      onChange({ subject: currentVal + emoji });
    }
  };

  // Fetch categories from preferences API (new 7-category system)
  const [categoryOptions, setCategoryOptions] = useState([
    { key: 'campaigns_marketing', label: 'Campaigns Marketing' },
    { key: 'transactional', label: 'Transactional' },
    { key: 'automation_events', label: 'Automation Events' },
    { key: 'scheduled_reminders', label: 'Scheduled Reminders' },
    { key: 'reports_analytics', label: 'Reports Analytics' },
    { key: 'billing_subscription', label: 'Billing Subscription' },
    { key: 'product_updates', label: 'Product Updates' },
  ]);
  useEffect(() => {
    preferencesApi.categories()
      .then((res) => {
        const cats = res?.data?.data ?? res?.data ?? [];
        if (Array.isArray(cats) && cats.length > 0) {
          const normalized = cats
            .map((c) => (typeof c === 'string' ? { key: c, label: c } : { key: c.key || c.name || c.category, label: c.label || c.key || c.name || '' }))
            .filter((c) => c.key);
          if (normalized.length > 0) setCategoryOptions(normalized);
        }
      })
      .catch(() => { /* keep fallback */ });
  }, []);

  const handleVariableTypeChange = (newType) => {
    // Reset variables when switching type
    if (newType === 'number') {
      onChange({
        variableType: newType,
        variables: [{ key: '1', type: 'number' }]
      });
    } else {
      onChange({
        variableType: newType,
        variables: [{ key: '', type: 'name' }]
      });
    }
  };

  const handleNameChange = (e) => {
    const name = e.target.value;
    // templateCode is optional — backend auto-generates from name if not provided
    onChange({ templateName: name });
  };

  return (
    <div className="emtpl-section">
      <div className="emtpl-row">
        <div className="emtpl-group full-width">
          <label className="em-label required">Template Name</label>
          <input
            type="text"
            className={`em-input${errors.templateName ? ' em-input-error' : ''}`}
            value={formData.templateName}
            onChange={handleNameChange}
            placeholder="e.g., Welcome Email"
          />
          {errors.templateName && <span className="em-error-text">{errors.templateName}</span>}
        </div>
      </div>



      <div className="emtpl-row">
        <div className="emtpl-group full-width">
          <label className="em-label">Category</label>
          <select
            className="em-select"
            value={formData.category || 'campaigns_marketing'}
            onChange={(e) => onChange({ category: e.target.value })}
          >
            {categoryOptions.map((cat) => (
              <option key={cat.key} value={cat.key}>{cat.label}</option>
            ))}
          </select>
          <span className="em-helper">Template category for organization and filtering</span>
        </div>
      </div>

      <div className="emtpl-row">
        <div className="emtpl-group full-width">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <label className="em-label required" style={{ marginBottom: 0 }}>Subject Line</label>
            <EmojiPickerButton onSelect={handleSubjectEmoji} />
          </div>
          <input
            ref={subjectInputRef}
            type="text"
            className={`em-input${errors.subject ? ' em-input-error' : ''}`}
            value={formData.subject}
            onChange={(e) => onChange({ subject: e.target.value })}
            placeholder="e.g., Welcome to our platform!"
          />
          {errors.subject && <span className="em-error-text">{errors.subject}</span>}
          <span className="em-helper">
            Subject line for your email
          </span>
        </div>
      </div>

      <div className="emtpl-row">
        <div className="emtpl-group">
          <label className="emtpl-checkbox">
            <input
              type="checkbox"
              checked={formData.isActive}
              onChange={(e) => onChange({ isActive: e.target.checked })}
            />
            <span>{formData.isActive ? 'Template is Active' : 'Template is Inactive'}</span>
          </label>
        </div>
      </div>

      <div className="emtpl-divider" />

      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '0.75rem' }}>
          <div>
            <h3 className="emtpl-subsection-title" style={{ margin: 0 }}>Template Variables</h3>
            <p className="emtpl-subsection-subtitle" style={{ margin: '2px 0 0 0' }}>
              Variables are auto-detected from Body content
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span className="em-label" style={{ marginBottom: 0, fontSize: '0.78rem' }}>Type:</span>
            <div className="emtpl-type-toggle">
              <button
                type="button"
                className={`emtpl-type-btn${(formData.variableType || 'name') === 'name' ? ' active' : ''}`}
                onClick={() => handleVariableTypeChange('name')}
              >
                Name
              </button>
              <button
                type="button"
                className={`emtpl-type-btn${(formData.variableType || 'name') === 'number' ? ' active' : ''}`}
                onClick={() => handleVariableTypeChange('number')}
              >
                Number
              </button>
            </div>
          </div>
        </div>
        <VariablesSection formData={formData} onChange={onChange} />
      </div>
    </div>
  );
};

export default BasicInfoSection;
